import dayjs from 'dayjs'

import FileOutlinedIcon from '@ant-design/icons/FileOutlined'
import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import usePopover from '@shared/hooks/src/usePopover'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

import { useUnmatchedLabDelete, useUnmatchedLabs, useUnmatchedLabsCount } from './UnmatchedLabs.hooks'

export default function UnmatchedLabs({ onSelect, onNotApplicable }) {
  const popover = usePopover()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useUnmatchedLabs()
  const unmatchedLabsCount = useUnmatchedLabsCount()

  const markNotApplicable = useUnmatchedLabDelete()

  const onMarkNotApplicable = useDialog({
    component: ConfirmationV2,
    props: ({ item, close }) => ({
      maxWidth: 'xs',
      variant: 'error',
      title: 'Delete the unmatched lab results?',
      description: 'This action cannot be undone',
      onReject: () => close(),
      onConfirm: () => {
        return markNotApplicable
          .mutateAsync(item)
          .then(() => onNotApplicable(item))
          .then(() => close())
      },
    }),
  })

  const showEmpty = !data || data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <>
      <Badge color="error" badgeContent={unmatchedLabsCount} invisible={!unmatchedLabsCount}>
        <Button
          id="unmatched-labs-btn"
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          variant="outlined"
          sx={{ backgroundColor: 'background.paper' }}
          data-testid="unmatched-labs-btn"
        >
          Unmatched Labs
        </Button>
      </Badge>
      <Popover
        anchorEl={popover.anchorRef.current}
        open={popover.open}
        onClose={popover.handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: { width: '440px' }, 'data-testid': 'unmatched-labs-popover' } }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 3, py: 2 }}>
          <Typography variant="h4">Unmatched Labs</Typography>
          {unmatchedLabsCount && <Chip label={unmatchedLabsCount} color="error" variant="light" size="small" />}
        </Stack>
        <Divider />
        {showEmpty && (
          <Box sx={{ p: 2 }} data-testid="unmatched-labs-empty">
            <Typography variant="h5" align="center" sx={{ color: 'text.secondary', height: 50, fontWeight: 'normal' }}>
              Good job! Everything is clear :)
            </Typography>
          </Box>
        )}
        {showData && (
          <Box sx={{ maxHeight: '360px', overflowY: 'auto' }}>
            <List disablePadding data-testid="unmatched-labs-list">
              {data.map((lab, index) => (
                <Lab
                  key={lab.id}
                  divider={index < data.length - 1}
                  lab={lab}
                  onSelect={() => {
                    onSelect(lab)
                    popover.handleClose()
                  }}
                  onNotApplicable={() => onMarkNotApplicable(lab.id)}
                />
              ))}
              {hasNextPage && (
                <Button
                  fullWidth
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  endIcon={<MoreOutlinedIcon rotate={90} />}
                  loadingPosition="end"
                  sx={{ textTransform: 'none' }}
                  data-testid="unmatched-labs-more"
                >
                  more
                </Button>
              )}
            </List>
          </Box>
        )}
      </Popover>
    </>
  )
}

function Lab({ divider, lab, onSelect, onNotApplicable }) {
  const testId = `lab-item-${lab.id}`
  const title = `${lab.patientFirstName} ${lab.patientLastName}, ${lab.patientDob ? dayjs(lab.patientDob).format('L') : 'DOB: N/A'}`

  return (
    <ListItemButton divider={divider} onClick={onSelect} data-testid={testId}>
      <Stack sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Stack>
            <Typography copy={title} variant="body2" data-testid={`${testId}-content`} sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
            {lab.createdAt && (
              <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
                {dayjs(lab.createdAt).format('MMM D, hh:mm A')}
              </Typography>
            )}
          </Stack>
          <Button
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              return onNotApplicable()
            }}
          >
            N/A
          </Button>
        </Stack>
        {lab.resultFiles?.length > 0 && (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {lab.resultFiles.map((file, i) => (
              <Chip
                key={i}
                label={file.fileName}
                variant="outlined"
                icon={<FileOutlinedIcon />}
                clickable
                component="a"
                href={file.url}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                data-testid={`${testId}-file-chip-${i}`}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </ListItemButton>
  )
}
